import { AuthorityInfoAccessExtension, AuthorityKeyIdentifierExtension, SubjectKeyIdentifierExtension } from '@peculiar/x509';
import { children, oidToString, readTlv, TAG } from '../bytes/der';
import { pemDecodeAll } from '../bytes/encoding';
import type { LogTransport } from '../transport/logTransport';
import { parseCertificate, type ParsedCert } from '../x509/parse';
import type { DiscoveredChain } from './types';

export const AIA_ID = 'aia';
const OID_SIGNED_DATA = '1.2.840.113549.1.7.2';

/** AIA caIssuers URLs of a certificate. */
export function aiaIssuerUrls(cert: ParsedCert): string[] {
  const aia = cert.cert.getExtension(AuthorityInfoAccessExtension);
  return aia?.caIssuers.filter((n) => n.type === 'url').map((n) => n.value) ?? [];
}

/** Extracts certificates from what an AIA URL returns: a DER certificate, PEM, or a PKCS#7 bundle. */
export function certsFromResponse(bytes: Uint8Array): Uint8Array[] {
  if (bytes.length === 0) return [];
  if (bytes[0] === 0x2d /* '-' */ || bytes[0] === 0x4d /* 'M' */) {
    return pemDecodeAll(new TextDecoder().decode(bytes)).map((b) => b.der);
  }
  const outer = readTlv(bytes, 0);
  if (outer.tag !== TAG.SEQUENCE) return [];
  const [first] = children(outer);
  if (first?.tag === TAG.SEQUENCE) return [bytes.subarray(0, outer.next)]; // Certificate ::= SEQUENCE { tbs SEQUENCE, … }
  if (first?.tag === TAG.OID && oidToString(first.content) === OID_SIGNED_DATA) {
    // ContentInfo { contentType, [0] EXPLICIT SignedData { version, digestAlgorithms, contentInfo, [0] IMPLICIT certificates, … } }
    const kids = [...children(outer)];
    const explicit = kids[1];
    if (!explicit || explicit.tag !== 0xa0) return [];
    const [signedData] = children(explicit);
    if (!signedData || signedData.tag !== TAG.SEQUENCE) return [];
    const certSet = [...children(signedData)].find((k) => k.tag === 0xa0);
    if (!certSet) return [];
    return [...children(certSet)].filter((c) => c.tag === TAG.SEQUENCE).map((c) => bytes.subarray(c.offset, c.end));
  }
  return [];
}

/** Does `issuer` plausibly issue `cert`? Subject/issuer DN match plus AKI/SKI when both are present. */
export function isIssuerOf(issuer: ParsedCert, cert: ParsedCert): boolean {
  if (issuer.subject !== cert.issuer) return false;
  const aki = cert.cert.getExtension(AuthorityKeyIdentifierExtension)?.keyId;
  const ski = issuer.cert.getExtension(SubjectKeyIdentifierExtension)?.keyId;
  return !aki || !ski || aki.toLowerCase() === ski.toLowerCase();
}

/**
 * Walks AIA caIssuers URLs upward until a self-signed certificate (or no URL / depth limit).
 * CA servers send no CORS headers and often use plain http, so this only works where the fetcher
 * is not a browser page (desktop app / proxy that allows it).
 */
export async function chainFromAia(chain: Uint8Array[], transport: LogTransport, opts: { maxDepth?: number; signal?: AbortSignal } = {}): Promise<DiscoveredChain> {
  const out = [...chain];
  const urls: string[] = [];
  let top = parseCertificate(out[out.length - 1]!);
  for (let depth = 0; depth < (opts.maxDepth ?? 6) && top.subject !== top.issuer; depth++) {
    const candidates = aiaIssuerUrls(top);
    if (candidates.length === 0) break;
    let next: { der: Uint8Array; parsed: ParsedCert } | undefined;
    let lastError: unknown;
    for (const url of candidates) {
      try {
        const res = await transport.request({ id: AIA_ID }, 'issuer', url, opts.signal ? { signal: opts.signal } : undefined);
        for (const der of certsFromResponse(res.body)) {
          const parsed = parseCertificate(der);
          if (isIssuerOf(parsed, top)) {
            next = { der, parsed };
            urls.push(url);
            break;
          }
        }
        if (next) break;
      } catch (e) {
        lastError = e;
      }
    }
    if (!next) {
      if (lastError) throw lastError;
      break;
    }
    out.push(next.der);
    top = next.parsed;
  }
  const subjects = out.map((d) => parseCertificate(d).subject);
  return { chain: out, subjects, endsAtRoot: top.subject === top.issuer, source: 'aia', detail: urls.join(' → ') || 'no AIA URL to follow' };
}
